/**
 * Customer-facing changelog: docs/help/changelog/<YYYY-MM-DD>.md, bundled into
 * the app at build time, so every production deploy ships the entries written
 * for it (docs/help/README.md § Changelog).
 *
 * Entry file:
 *   ---
 *   date: 2026-10-20
 *   title: Manual orders on the Order Queue, and more
 *   image: manual-orders.jpg      # optional, from docs/help/changelog/images/
 *   covers: <40-hex SHA>          # optional; read by the release-notes gate, ignored here
 *   ---
 *   - **Manual orders on the Order Queue.** One or two plain sentences. [guide: some-guide-slug]
 *
 * The first three items are the "What's new" highlights. An optional
 * <date>.zh.md beside it is used for Chinese viewers (English otherwise).
 * scripts/check-help-docs.mjs validates the files in CI (validateChangelog in
 * scripts/help-guides/guide-format.mjs parses the same format: KEEP IN LOCKSTEP).
 * `[guide: none]` marks a change with no guide.
 */

export interface ChangelogItem {
  /** Markdown-lite text (SimpleMarkdown), without the guide marker. */
  text: string;
  /** Help Center article slug, when the change has a guide. */
  guide: string | null;
}

export interface ChangelogEntry {
  date: string;
  title: string;
  /** Header photo URL for this release, or null for the default banner. */
  image: string | null;
  items: ChangelogItem[];
}

const IMAGES = import.meta.glob<string>('../../../../../../docs/help/changelog/images/*', {
  query: '?url',
  import: 'default',
  eager: true,
});

const GUIDE = /\s*\[guide:\s*([a-z0-9-]+)\]\s*$/;

export function parseChangelog(raw: string, images: Record<string, string> = IMAGES): ChangelogEntry | null {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  if (lines[0]?.trim() !== '---') return null;
  const end = lines.indexOf('---', 1);
  if (end === -1) return null;
  const meta: Record<string, string> = {};
  for (const l of lines.slice(1, end)) {
    const m = l.match(/^([a-z_]+):\s*(.*)$/);
    if (m?.[1]) meta[m[1]] = (m[2] ?? '').trim().replace(/^(["'])(.*)\1$/, '$2');
  }
  const { date, title } = meta;
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !title) return null;

  const items: ChangelogItem[] = [];
  for (const l of lines.slice(end + 1)) {
    if (/^- /.test(l)) items.push({ text: l.slice(2).trim(), guide: null });
    else if (/^\s{2,}\S/.test(l)) {
      const last = items[items.length - 1];
      if (last) last.text += `\n${l.trim()}`;
    }
  }
  for (const item of items) {
    const g = item.text.match(GUIDE);
    if (g?.[1]) {
      item.guide = g[1] === 'none' ? null : g[1];
      item.text = item.text.replace(GUIDE, '');
    }
  }
  const image = meta.image ? (images[`../../../../../../docs/help/changelog/images/${meta.image}`] ?? null) : null;
  return { date, title, image, items };
}

const FILES = import.meta.glob<string>('../../../../../../docs/help/changelog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/** Entries newest first, in the viewer's language where a translation exists. */
export function loadChangelog(language: 'en' | 'zh', files: Record<string, string> = FILES): ChangelogEntry[] {
  const byDate = new Map<string, { en?: string; zh?: string }>();
  for (const [path, raw] of Object.entries(files)) {
    const m = path.match(/(\d{4}-\d{2}-\d{2})(\.zh)?\.md$/);
    const date = m?.[1];
    if (!date) continue;
    const slot = byDate.get(date) ?? {};
    slot[m?.[2] ? 'zh' : 'en'] = raw;
    byDate.set(date, slot);
  }
  return [...byDate.entries()]
    .map(([, s]) => parseChangelog((language === 'zh' && s.zh) || s.en || ''))
    .filter((e): e is ChangelogEntry => e !== null)
    .sort((a, b) => b.date.localeCompare(a.date));
}
